import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../../components/layout/Layout';
import { fetchLectureCategories, LectureCategory } from '../../services/lectureCategoryApi';
import { fetchLecturesByCategory, fetchAllLectures } from '../../services/lectureInfoApi';
import { LectureInfo, LectureInfoListResponse } from '../../types';
import { searchAllLectures, searchLecturesByCategory, LectureSearchItem } from '../../services/searchApi';
import { pickThumbnail } from '../../utils/thumbnail';

const ClassCategory: React.FC = () => {
  const [searchValue, setSearchValue] = useState('');
  const [categories, setCategories] = useState<LectureCategory[]>([]);
  const [lectureInfoList, setLectureInfoList] = useState<LectureInfo[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [lecturesLoading, setLecturesLoading] = useState(false);
  
  // 검색 상태
  const [searchResults, setSearchResults] = useState<LectureSearchItem[]>([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  
  // 무한스크롤 관련 상태 (전체보기용)
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const PAGE_SIZE = 18; // 화면을 꽉 채우기 위해 18개로 증가 (3열 × 6행)
  
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
   
  // URL에서 카테고리 파라미터 가져오기
  const getActiveCategory = useCallback(() => {
    const params = new URLSearchParams(location.search);
    return params.get('category') || 'all';
  }, [location.search]);
  
  // URL에서 검색 키워드 가져오기
  const getSearchKeyword = useCallback(() => {
    return searchParams.get('keyword') || '';
  }, [searchParams]);
  
  const [activeCategory, setActiveCategory] = useState(getActiveCategory());

  // 검색 수행 함수
  const performSearch = useCallback(async (keyword: string, categoryRouteName: string) => {
    if (!keyword.trim()) {
      setIsSearchMode(false);
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    setSearchError(null);
    setIsSearchMode(true);
    
    try {
      let results: LectureSearchItem[];
      
      if (categoryRouteName === 'all') {
        // 전체보기인 경우
        results = await searchAllLectures(keyword);
      } else {
        // 특정 카테고리인 경우 - ID 찾기
        const selectedCategory = categories.find(cat => cat.route_name === categoryRouteName);
        if (!selectedCategory) {
          throw new Error('카테고리를 찾을 수 없습니다.');
        }
        results = await searchLecturesByCategory(selectedCategory.id, keyword);
      }
      
      setSearchResults(results);
    } catch (error) {
      /// console.error('검색 중 오류 발생:', error);
      setSearchError('검색 중 오류가 발생했습니다. 다시 시도해주세요.');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [categories]);

  // 강의 정보 목록 가져오기 (모든 카테고리에서 페이지네이션 지원)
  const loadLectureInfoList = useCallback(async (categoryRouteName: string, pageToLoad: number, isLoadMore: boolean = false) => {
    if (isLoadMore) {
      setIsLoadingMore(true);
    } else {
      setLecturesLoading(true);
    }
    
    try {
      // /// console.log(`[ClassCategory] 강의 정보 로딩 시작 - 카테고리: ${categoryRouteName}, 페이지: ${pageToLoad}`);
      
      let response: LectureInfoListResponse;
      
      if (categoryRouteName === 'all') {
        // 전체보기인 경우
        // /// console.log('[ClassCategory] 전체 강의 API 호출');
        response = await fetchAllLectures(pageToLoad, PAGE_SIZE);
      } else {
        // 특정 카테고리인 경우도 페이지네이션 API 사용
        // /// console.log(`[ClassCategory] 카테고리별 강의 API 호출: ${categoryRouteName}, 페이지: ${pageToLoad}`);
        response = await fetchLecturesByCategory(categoryRouteName, pageToLoad, PAGE_SIZE);
      }
      
      /// console.log(`클래스 카테고리 API 응답 결과:`, {req: {categoryRouteName, pageToLoad, PAGE_SIZE}, response});
      
      if (response.code === 200) {
        // 모든 카테고리에서 페이지네이션 처리
        if (isLoadMore) {
          setLectureInfoList(prev => [...prev, ...response.data]);
          setCurrentPage(pageToLoad);
        } else {
          setLectureInfoList(response.data);
          setCurrentPage(pageToLoad);
        }
        
        // 더 로드할 데이터가 있는지 확인
        if (response.pagination) {
          const nextPage = pageToLoad + 1;
          setHasMoreData(nextPage < response.pagination.totalPages);
        } else {
          // pagination 정보가 없으면 더 이상 데이터가 없다고 가정
          setHasMoreData(false);
        }
        
        // /// console.log(`[ClassCategory] 강의 정보 목록 로드 완료 (${response.data.length}개):`, response.data);
      } else {
        /// console.log(`[ClassCategory] 응답 코드가 200이 아님:`, response.code);
        if (!isLoadMore) {
          setLectureInfoList([]);
        }
      }
    } catch (error) {
      /// console.error(`[ClassCategory] 강의 정보 목록 로딩 실패:`, error);
      // 404 오류 등의 경우 빈 배열로 설정
      if (!isLoadMore) {
        setLectureInfoList([]);
      }
    } finally {
      if (isLoadMore) {
        setIsLoadingMore(false);
      } else {
        setLecturesLoading(false);
      }
    }
  }, [PAGE_SIZE]);

  // 강의 카테고리 데이터 가져오기
  const loadCategories = async () => {
    setCategoriesLoading(true);
    try {
      // /// console.log('[ClassCategory] 카테고리 데이터 로딩 시작');
      const response = await fetchLectureCategories();
      /// console.log('클래스 카테고리 API 응답 데이터:', {response});
      
      if (response.code === 200) {
        setCategories(response.data);
        // /// console.log(`[ClassCategory] 강의 카테고리 데이터 로드 완료 (${response.data.length}개):`, response.data);
      } else {
        /// console.log(`[ClassCategory] 카테고리 응답 코드가 200이 아님:`, response.code);
      }
    } catch (error) {
      /// console.error('[ClassCategory] 강의 카테고리 데이터 로딩 실패:', error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // URL 파라미터 변경 감지
  useEffect(() => {
    const newActiveCategory = getActiveCategory();
    const keyword = getSearchKeyword();
    
    setActiveCategory(newActiveCategory);
    setSearchValue(keyword);
    
    // 검색 키워드가 있으면 검색 수행
    if (keyword && categories.length > 0) {
      performSearch(keyword, newActiveCategory);
    } else {
      setIsSearchMode(false);
      setSearchResults([]);
    }
  }, [location.search, categories, performSearch, getActiveCategory, getSearchKeyword]);

  // 카테고리 변경 시 강의 목록 로드 (검색 모드가 아닐 때만)
  useEffect(() => {
    if (categories.length > 0 && !isSearchMode) {
      // 현재 활성화된 카테고리의 route_name으로 강의 목록 로드
      // /// console.log('카테고리 변경:', activeCategory);
      loadLectureInfoList(activeCategory, 0, false);
    }
  }, [activeCategory, categories, isSearchMode, loadLectureInfoList]);

  // 컴포넌트 마운트 시 카테고리 데이터 로드
  useEffect(() => {
    loadCategories();
  }, []);

  // 무한스크롤 구현 (검색 모드가 아닐 때만 작동)
  useEffect(() => {
    if (isSearchMode) return; // 검색 모드에서는 무한스크롤 비활성화
    
    const handleScroll = () => {
      // 페이지 하단에서 400px 위에 도달하면 더 로드 (더 여유롭게 트리거)
      if (
        window.innerHeight + document.documentElement.scrollTop 
        >= document.documentElement.offsetHeight - 400
      ) {
        if (!isLoadingMore && hasMoreData && !lecturesLoading) {
          // /// console.log('무한스크롤 트리거 - 현재 페이지:', currentPage);
          loadLectureInfoList(activeCategory, currentPage + 1, true);
        }
      }
    };

    // 모든 카테고리에서 스크롤 이벤트 등록
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      // 컴포넌트 언마운트나 카테고리 변경 시 이벤트 리스너 제거
      window.removeEventListener('scroll', handleScroll);
    };
  }, [activeCategory, currentPage, hasMoreData, isLoadingMore, lecturesLoading, isSearchMode, loadLectureInfoList]);

  // 전체보기 카테고리 추가
  const allCategories = [
    { id: 0, name: '전체보기', route_name: 'all', level: 0, parent_category_id: null, image_url: null, optimized_image_url: null },
    ...categories
  ];

  // 카테고리 클릭 핸들러
  const handleCategoryClick = useCallback((routeName: string) => {
    // 카테고리 변경 시 즉시 상태 초기화
    setLectureInfoList([]);
    setCurrentPage(0);
    setHasMoreData(true);
    setIsLoadingMore(false);
    setLecturesLoading(false); // 먼저 false로 설정
    
    // 검색 상태 초기화
    setIsSearchMode(false);
    setSearchResults([]);
    setSearchError(null);
    setSearchValue(''); // 검색 입력값도 초기화
    
    // URL 변경 (검색 파라미터 제거)
    const params = new URLSearchParams();
    if (routeName !== 'all') {
      params.set('category', routeName);
    }
    
    setActiveCategory(routeName);
    if (params.toString()) {
      navigate(`/class/category?${params.toString()}`);
    } else {
      navigate('/class/category');
    }
    
    // 페이지 최상단으로 스크롤
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigate]);

  // 강의 카드 클릭 핸들러
  const handleLectureClick = useCallback((lectureId: number) => {
    navigate(`/class/lecture/${lectureId}`);
  }, [navigate]);
  
  // 검색 결과를 LectureInfo 형식으로 변환
  const convertSearchResultToLectureInfo = (item: LectureSearchItem): LectureInfo => {
    return {
      id: item.lecture_info.id,
      title: item.lecture_info.title,
      caption: item.lecturer_info.department, // caption을 강연자 소속으로 사용
      thumbnail_url: pickThumbnail(item.lecture_info),
      name: item.lecture_info.title, // name 필드를 title로 매핑
      tags: item.lecture_info.tags,
      introduction: item.lecturer_info.department, // introduction 필드를 lecturer department로 매핑
      lecturer_name: item.lecturer_info.name,
      lecturer_introduction: item.lecturer_info.department,
      lecturer_id: item.lecturer_info.id,
      lecturer_image_url: item.lecturer_info.image_url
    };
  };

  // 검색 핸들러
  const handleSearchSubmit = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const searchTerm = (e.target as HTMLInputElement).value.trim();
      
      if (searchTerm) {
        // URL 파라미터 업데이트
        const params = new URLSearchParams(searchParams);
        params.set('keyword', searchTerm);
        setSearchParams(params);
      } else {
        // 검색어가 비어있으면 검색 파라미터 제거
        const params = new URLSearchParams(searchParams);
        params.delete('keyword');
        setSearchParams(params);
      }
    }
  }, [searchParams, setSearchParams]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  }, []);

  return (
    <Layout>
      <Container>
        {/* 사이드바와 메인 콘텐츠 컨테이너 */}
        <MainContainer>
          {/* 사이드바 */}
          <Sidebar>
            <SidebarContent>
              {categoriesLoading ? (
                // 스켈레톤 UI
                Array.from({ length: 7 }).map((_, index) => (
                  <SidebarItemSkeleton key={index} />
                ))
              ) : (
                allCategories.map((category) => (
                  <SidebarItem 
                    key={category.id}
                    active={activeCategory === category.route_name}
                    onClick={() => handleCategoryClick(category.route_name)}
                  >
                    {category.name}
                  </SidebarItem>
                ))
              )}
            </SidebarContent>
          </Sidebar>

          {/* 메인 콘텐츠 */}
          <ContentArea>
            {/* 페이지 제목 */}
            <PageTitle>
              {allCategories.find(cat => cat.route_name === activeCategory)?.name || '카테고리'}
            </PageTitle>

            {/* 브레드크럼과 검색바 */}
            <NavigationContainer>
              <BreadcrumbContainer>
                <BreadcrumbItem>홈</BreadcrumbItem>
                <BreadcrumbChevron>
                  <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                    <path 
                      d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                      stroke="rgba(55, 56, 60, 0.61)" 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </BreadcrumbChevron>
                <BreadcrumbItem>클래스</BreadcrumbItem>
                <BreadcrumbChevron>
                  <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                    <path 
                      d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                      stroke="rgba(55, 56, 60, 0.61)" 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </BreadcrumbChevron>
                <BreadcrumbItem>
                  {allCategories.find(cat => cat.route_name === activeCategory)?.name || '카테고리'}
                </BreadcrumbItem>
              </BreadcrumbContainer>

              {/* 검색바 */}
              <SearchContainer>
                <SearchBox>
                  <SearchIcon>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M14.6667 14.6667L11.2 11.2M12.6667 7.33333C12.6667 10.2789 10.2789 12.6667 7.33333 12.6667C4.38781 12.6667 2 10.2789 2 7.33333C2 4.38781 4.38781 2 7.33333 2C10.2789 2 12.6667 4.38781 12.6667 7.33333Z" stroke="rgba(55, 56, 60, 0.28)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </SearchIcon>
                  <SearchInput 
                    type="text" 
                    placeholder="관심 주제, 강의 찾기"
                    value={searchValue}
                    onChange={handleSearchChange}
                    onKeyUp={handleSearchSubmit}
                  />
                </SearchBox>
              </SearchContainer>
            </NavigationContainer>

            {/* 검색 결과 헤더 */}
            {/* {isSearchMode && (
              <SearchResultHeader>
                <SearchResultTitle>
                  '{searchValue}' 검색 결과
                </SearchResultTitle>
              </SearchResultHeader>
            )} */}

            {/* 클래스 카드 섹션 */}
            <ClassSection>
              <ClassGridContainer>
                {(isSearchMode && searchLoading) || (!isSearchMode && lecturesLoading) ? (
                  // 스켈레톤 UI - 18개 카드 (6줄 × 3개)
                  Array.from({ length: 18 }).map((_, index) => (
                    <ClassCard key={`skeleton-${index}`} style={{ pointerEvents: 'none' }}>
                      <ClassThumbnail>
                        <ClassImageSkeleton />
                      </ClassThumbnail>
                      <ClassInfo>
                        <TagSkeleton />
                        <ClassTitleSkeleton />
                        <ClassCaptionSkeleton />
                      </ClassInfo>
                    </ClassCard>
                  ))
                ) : isSearchMode ? (
                  // 검색 모드일 때
                  searchError ? (
                    <EmptyState>
                      <EmptyStateDescription>{searchError}</EmptyStateDescription>
                    </EmptyState>
                  ) : searchResults.length === 0 ? (
                    <EmptyState>
                      <EmptyStateDescription>
                        '{searchValue}'에 대한 검색 결과가 없습니다.
                      </EmptyStateDescription>
                    </EmptyState>
                  ) : (
                    searchResults.map((item) => {
                      const lecture = convertSearchResultToLectureInfo(item);
                      return (
                        <ClassCard key={lecture.id} onClick={() => handleLectureClick(lecture.id)}>
                          <ClassThumbnail>
                            <ClassImage 
                              src={lecture?.thumbnail_url || '/images/no-image-dummy.png'} 
                              alt="클래스 썸네일" 
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = '/images/no-image-dummy.png';
                              }}
                            />
                          </ClassThumbnail>
                          <ClassInfo>
                            {/* 태그 표시 */}
                            {lecture.tags && lecture.tags.length > 0 && (
                              <TagContainer>
                                {lecture.tags.map((tag, index) => (
                                  <Tag key={index}>{tag}</Tag>
                                ))}
                              </TagContainer>
                            )}
                            <ClassTitle>{lecture.title}</ClassTitle>
                            <ClassExtraCaption>{lecture.lecturer_name} · {lecture.lecturer_introduction}</ClassExtraCaption>
                          </ClassInfo>
                        </ClassCard>
                      );
                    })
                  )
                ) : lectureInfoList.length === 0 ? (
                  // 빈 상태 메시지 (기본 모드)
                  <EmptyState>
                    <EmptyStateDescription>
                      해당 카테고리에 등록된 강의가 없습니다.<br />
                      곧 다양한 강의를 만나보실 수 있습니다.
                    </EmptyStateDescription>
                  </EmptyState>
                ) : (
                  // 기본 강의 목록
                  lectureInfoList.map((lecture) => (
                    <ClassCard key={lecture.id} onClick={() => handleLectureClick(lecture.id)}>
                      <ClassThumbnail>
                        <ClassImage 
                          src={lecture?.thumbnail_url || '/images/no-image-dummy.png'} 
                          alt="클래스 썸네일" 
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/images/no-image-dummy.png';
                          }}
                        />
                      </ClassThumbnail>
                      <ClassInfo>
                        {/* 태그 표시 */}
                        {lecture.tags && lecture.tags.length > 0 && (
                          <TagContainer>
                            {lecture.tags.map((tag, index) => (
                              <Tag key={index}>{tag}</Tag>
                            ))}
                          </TagContainer>
                        )}
                        <ClassTitle>{lecture.title}</ClassTitle>
                        <ClassExtraCaption>{lecture.lecturer_name} · {lecture.lecturer_introduction}</ClassExtraCaption>
                      </ClassInfo>
                    </ClassCard>
                  ))
                )}
              </ClassGridContainer>
              
              {/* 무한스크롤 로딩 인디케이터 (검색 모드가 아닐 때만 표시) */}
              {!isSearchMode && !lecturesLoading && (
                <LoadMoreSection>
                  {isLoadingMore && (
                    <LoadMoreSkeleton>
                      <div>더 많은 강의를 불러오는 중...</div>
                    </LoadMoreSkeleton>
                  )}
                  {!hasMoreData && lectureInfoList.length > 0 && (
                    <div style={{ 
                      textAlign: 'center', 
                      color: 'rgba(55, 56, 60, 0.61)', 
                      fontSize: '16px',
                      padding: '40px 0'
                    }}>
                      모든 강의를 확인했습니다.
                    </div>
                  )}
                </LoadMoreSection>
              )}
            </ClassSection>
          </ContentArea>
        </MainContainer>
      </Container>
    </Layout>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  margin: 0;
  padding: 0;
`;

const MainContainer = styled.div`
  max-width: ${({ theme }) => theme.layout.containerWidth};
  margin: 0 auto;
  padding: 64px ${({ theme }) => theme.layout.containerPadding} 128px;
  display: flex;
  gap: 32px;
  position: relative;
  
  @media (max-width: 1024px) {
    padding: 64px ${({ theme }) => theme.layout.containerPaddingTablet} 128px;
    gap: 24px;
  }
  
  @media (max-width: 768px) {
    padding: 64px ${({ theme }) => theme.layout.containerPaddingMobile} 100px;
    flex-direction: column;
    gap: 16px;
  }
`;

// 사이드바 스타일
const Sidebar = styled.nav`
  width: 242px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  
  @media (max-width: 768px) {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const SidebarItem = styled.div<{ active?: boolean }>`
  width: 100%;
  height: 52px;
  padding: 12px 12px 12px 16px;
  border-radius: 12px;
  overflow: hidden;
  background: transparent;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.2s ease;
  
  /* 텍스트 스타일 */
  color: ${props => props.active ? 'var(--Label-Normal, #171719)' : 'var(--Label-Assistive, rgba(55, 56, 60, 0.28))'};
  font-size: 20px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 28px;
  letter-spacing: -0.01em;
  word-wrap: break-word;
  
  /* 새로운 chevron 아이콘 스타일 */
  &::after {
    content: '';
    width: 24px;
    height: 24px;
    background-image: ${props => props.active 
      ? `url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.8637 4.86321C8.51223 5.21469 8.51223 5.78453 8.8637 6.13601L14.7273 11.9996L8.8637 17.8632C8.51223 18.2147 8.51223 18.7845 8.8637 19.136C9.21517 19.4875 9.78502 19.4875 10.1365 19.136L16.6365 12.636C16.988 12.2845 16.988 11.7147 16.6365 11.3632L10.1365 4.86321C9.78502 4.51174 9.21517 4.51174 8.8637 4.86321Z' fill='%23171719'/%3E%3C/svg%3E")`
      : `url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.8637 4.86321C8.51223 5.21469 8.51223 5.78453 8.8637 6.13601L14.7273 11.9996L8.8637 17.8632C8.51223 18.2147 8.51223 18.7845 8.8637 19.136C9.21517 19.4875 9.78502 19.4875 10.1365 19.136L16.6365 12.636C16.988 12.2845 16.988 11.7147 16.6365 11.3632L10.1365 4.86321C9.78502 4.51174 9.21517 4.51174 8.8637 4.86321Z' fill='rgba(55, 56, 60, 0.28)'/%3E%3C/svg%3E")`
    };
    background-repeat: no-repeat;
    background-position: center;
    transition: all 0.2s ease;
  }
  
  /* hover 효과 */
  &:hover {
    color: var(--Label-Normal, #171719);
    
    &::after {
      background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.8637 4.86321C8.51223 5.21469 8.51223 5.78453 8.8637 6.13601L14.7273 11.9996L8.8637 17.8632C8.51223 18.2147 8.51223 18.7845 8.8637 19.136C9.21517 19.4875 9.78502 19.4875 10.1365 19.136L16.6365 12.636C16.988 12.2845 16.988 11.7147 16.6365 11.3632L10.1365 4.86321C9.78502 4.51174 9.21517 4.51174 8.8637 4.86321Z' fill='%23171719'/%3E%3C/svg%3E");
    }
  }
  
  @media (max-width: 768px) {
    flex: 0 0 auto;
    padding: 8px 12px;
    font-size: 14px;
    height: auto;
    
    &::after {
      display: none;
    }
  }
`;

// 콘텐츠 영역 스타일
const ContentArea = styled.main`
  width: 894px;
  max-width: 894px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-left: auto;
  
  @media (max-width: 1200px) {
    width: 100%;
    max-width: none;
    margin-left: 0;
  }
  
  @media (max-width: 768px) {
    gap: 24px;
  }
`;

const PageTitle = styled.h1`
  color: #171719;
  font-size: 40px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 54.01px;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 44px;
  }
`;

// 새로운 네비게이션 컨테이너 스타일
const NavigationContainer = styled.div`
  width: 100%;
  height: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
    gap: 16px;
    align-items: flex-start;
  }
`;

// 브레드크럼 스타일
const BreadcrumbContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const BreadcrumbItem = styled.span<{ active?: boolean }>`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const BreadcrumbChevron = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// 검색바 스타일 (브레드크럼 내부)
const SearchContainer = styled.div`
  width: 282px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 10px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchBox = styled.div`
  align-self: stretch;
  padding: 8px;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.22);
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  display: inline-flex;
`;

const SearchIcon = styled.div`
  height: 16px;
  padding-left: 2px;
  padding-right: 2px;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: #171719;
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16.01px;
  letter-spacing: 0.30px;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
`;

// 클래스 섹션 스타일 (기존 컴포넌트 재활용)
const ClassSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

// 새로운 그리드 스타일
const ClassGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const ClassRow = styled.div`
  display: flex;
  gap: 24px;
  
  @media (max-width: 1024px) {
    gap: 16px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const ClassCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  cursor: pointer;
`;

const ClassThumbnail = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.08);
  aspect-ratio: 3/2;
`;

const ClassImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  user-select: none;
  pointer-events: none;
  -webkit-user-drag: none;
  -webkit-user-select: none;
  transition: transform 0.3s ease;
  
  ${ClassCard}:hover & {
    transform: scale(1.05);
  }
`;

const ClassInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ClassTitle = styled.h3`
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  margin: 0;
`;

const ClassCaption = styled.p`
  color: rgba(55, 56, 60, 0.61);
  font-size: 13px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.25px;
  margin: 0;
`;

const ClassExtraCaption = styled.p`
  color: rgba(55, 56, 60, 0.61);
  font-size: 13px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.25px;
  margin: 0;
`;

// 태그 컨테이너와 태그 스타일 추가
const TagContainer = styled.div`
  align-self: stretch;
  overflow: hidden;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 6px;
  display: flex;
  flex-wrap: wrap;
`;

const Tag = styled.div`
  height: 20px;
  padding-left: 6px;
  padding-right: 6px;
  background: rgba(68, 129, 129, 0.08);
  border-radius: 6px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  display: flex;
  color: var(--Primary-Normal, #448181);
  font-size: 11px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0.34px;
  word-wrap: break-word;
`;

const TagSkeleton = styled.div`
  width: 50px;
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 6px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

// 스켈레톤 UI 컴포넌트들
const SidebarItemSkeleton = styled.div`
  padding: 12px 16px;
  border-radius: 12px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  height: 48px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
  
  @media (max-width: 768px) {
    flex: 0 0 auto;
    height: 36px;
  }
`;

const ClassImageSkeleton = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const ClassTitleSkeleton = styled.div`
  width: 80%;
  height: 24px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const ClassCaptionSkeleton = styled.div`
  width: 60%;
  height: 18px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  animation-delay: 0.2s;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

// 빈 상태 메시지 컴포넌트들
const EmptyState = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    font-size: 40px;
    margin-bottom: 20px;
  }
`;

const EmptyStateTitle = styled.h3`
  color: #171719;
  font-size: 24px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 1.4;
  margin: 0 0 16px 0;
  
  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 12px;
  }
`;

const EmptyStateDescription = styled.p`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 1.5;
  margin: 0;
  max-width: 400px;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

// 무한스크롤 로딩 컴포넌트
const LoadMoreSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 48px;
  
  @media (max-width: 768px) {
    margin-top: 32px;
  }
`;

const LoadMoreSkeleton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 32px;
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
`;

// 검색 결과 헤더 스타일
const SearchResultHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
`;

const SearchResultTitle = styled.h2`
  color: #171719;
  font-size: 24px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 32px;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 20px;
    line-height: 28px;
  }
`;

export default ClassCategory;
